import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Plus, Edit, CheckCircle, Trash2 } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

const initialPlans = [
  {
    id: 'plan-001',
    name: 'Basic',
    price: 149,
    billingCycle: 'monthly',
    features: {
      invoicesPerMonth: 1000,
      apiCallsPerMinute: 100,
      erpIntegrations: ['Tally', 'Zoho'],
      support: 'Email',
      reporting: 'Basic',
      users: 2,
      storageMonths: 6,
      customIntegration: false,
      dedicatedSupport: false,
      apiAccess: false
    },
    active: true,
    clients: 45
  },
  {
    id: 'plan-002',
    name: 'Standard',
    price: 299,
    billingCycle: 'monthly',
    features: {
      invoicesPerMonth: 5000,
      apiCallsPerMinute: 500,
      erpIntegrations: ['SAP', 'Oracle', 'Tally', 'Zoho'],
      support: 'Email & Phone',
      reporting: 'Advanced',
      users: 5,
      storageMonths: 12,
      customIntegration: false,
      dedicatedSupport: false,
      apiAccess: true
    },
    active: true,
    clients: 62
  },
  {
    id: 'plan-003',
    name: 'Premium',
    price: 499,
    billingCycle: 'monthly',
    features: {
      invoicesPerMonth: 0, // Unlimited
      apiCallsPerMinute: 1000,
      erpIntegrations: ['SAP', 'Oracle', 'Tally', 'Zoho', 'Dynamics'],
      support: '24/7 Priority',
      reporting: 'Custom',
      users: 0, // Unlimited
      storageMonths: 24,
      customIntegration: true,
      dedicatedSupport: true,
      apiAccess: true
    },
    active: true,
    clients: 40
  },
  {
    id: 'plan-004',
    name: 'Enterprise',
    price: 999,
    billingCycle: 'monthly',
    features: {
      invoicesPerMonth: 0,
      apiCallsPerMinute: 0, // Unlimited
      erpIntegrations: ['All ERPs + Custom'],
      support: 'Dedicated Account Manager',
      reporting: 'Custom + White Label',
      users: 0,
      storageMonths: 0, // Unlimited
      customIntegration: true,
      dedicatedSupport: true,
      apiAccess: true
    },
    active: false,
    clients: 0
  }
];

export default function PlansManagement() {
  const [plans, setPlans] = useState(initialPlans);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [editForm, setEditForm] = useState<any>({});

  const handleEditPlan = (plan: any) => {
    setSelectedPlan(plan);
    setEditForm({ ...plan });
    setIsEditDialogOpen(true);
  };

  const handleSavePlan = () => {
    setPlans(plans.map(p => p.id === editForm.id ? editForm : p));
    setIsEditDialogOpen(false);
    toast.success('Plan updated successfully');
  };

  const handleTogglePlan = (id: string) => {
    setPlans(plans.map(p => p.id === id ? { ...p, active: !p.active } : p));
    toast.success('Plan status updated');
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl text-gray-900">Plans Management</h1>
          <p className="text-gray-600 mt-1">Configure and customize subscription plans</p>
        </div>
        <Button className="bg-[#0052CC] hover:bg-[#0041A3]">
          <Plus className="w-4 h-4 mr-2" />
          Create New Plan
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Total Plans</p>
            <p className="text-2xl text-gray-900 mt-2">{plans.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Active Plans</p>
            <p className="text-2xl text-gray-900 mt-2">{plans.filter(p => p.active).length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Total Subscriptions</p>
            <p className="text-2xl text-gray-900 mt-2">{plans.reduce((acc, p) => acc + p.clients, 0)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-gray-600">Monthly Revenue</p>
            <p className="text-2xl text-gray-900 mt-2">
              ${plans.reduce((acc, p) => acc + (p.price * p.clients), 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className={`${!plan.active ? 'opacity-60' : ''}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{plan.name}</CardTitle>
                {plan.active ? (
                  <Badge className="bg-green-500">Active</Badge>
                ) : (
                  <Badge variant="secondary">Inactive</Badge>
                )}
              </div>
              <div className="mt-4">
                <span className="text-3xl text-gray-900">${plan.price}</span>
                <span className="text-gray-600">/{plan.billingCycle}</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-3">Features:</p>
                <ul className="space-y-2">
                  <li className="text-sm flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{plan.features.invoicesPerMonth === 0 ? 'Unlimited' : plan.features.invoicesPerMonth} invoices/month</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{plan.features.apiCallsPerMinute === 0 ? 'Unlimited' : plan.features.apiCallsPerMinute} API calls/min</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{plan.features.users === 0 ? 'Unlimited' : plan.features.users} users</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{plan.features.support} support</span>
                  </li>
                  <li className="text-sm flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>{plan.features.reporting} reporting</span>
                  </li>
                  {plan.features.customIntegration && (
                    <li className="text-sm flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Custom integrations</span>
                    </li>
                  )}
                </ul>
              </div>

              <div className="pt-4 border-t space-y-3">
                <p className="text-sm text-gray-600">
                  Active Clients: <span className="text-gray-900">{plan.clients}</span>
                </p>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => handleEditPlan(plan)}
                  >
                    <Edit className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleTogglePlan(plan.id)}
                  >
                    {plan.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Plan Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Plan - {selectedPlan?.name}</DialogTitle>
            <DialogDescription>
              Customize plan features, limits, and pricing for this subscription tier.
            </DialogDescription>
          </DialogHeader>
          
          {editForm && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Plan Name</Label>
                  <Input 
                    value={editForm.name || ''} 
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price (USD)</Label>
                  <Input 
                    type="number"
                    value={editForm.price || 0} 
                    onChange={(e) => setEditForm({ ...editForm, price: parseFloat(e.target.value) })}
                  />
                </div>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Feature Limits</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Invoices Per Month (0 = Unlimited)</Label>
                      <Input 
                        type="number"
                        value={editForm.features?.invoicesPerMonth || 0}
                        onChange={(e) => setEditForm({ 
                          ...editForm, 
                          features: { ...editForm.features, invoicesPerMonth: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>API Calls Per Minute (0 = Unlimited)</Label>
                      <Input 
                        type="number"
                        value={editForm.features?.apiCallsPerMinute || 0}
                        onChange={(e) => setEditForm({ 
                          ...editForm, 
                          features: { ...editForm.features, apiCallsPerMinute: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Number of Users (0 = Unlimited)</Label>
                      <Input 
                        type="number"
                        value={editForm.features?.users || 0}
                        onChange={(e) => setEditForm({ 
                          ...editForm, 
                          features: { ...editForm.features, users: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Storage (Months, 0 = Unlimited)</Label>
                      <Input 
                        type="number"
                        value={editForm.features?.storageMonths || 0}
                        onChange={(e) => setEditForm({ 
                          ...editForm, 
                          features: { ...editForm.features, storageMonths: parseInt(e.target.value) }
                        })}
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <Label>Custom Integrations</Label>
                      <Switch 
                        checked={editForm.features?.customIntegration || false}
                        onCheckedChange={(checked) => setEditForm({ 
                          ...editForm, 
                          features: { ...editForm.features, customIntegration: checked }
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <Label>Dedicated Support</Label>
                      <Switch 
                        checked={editForm.features?.dedicatedSupport || false}
                        onCheckedChange={(checked) => setEditForm({ 
                          ...editForm, 
                          features: { ...editForm.features, dedicatedSupport: checked }
                        })}
                      />
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <Label>API Access</Label>
                      <Switch 
                        checked={editForm.features?.apiAccess || false}
                        onCheckedChange={(checked) => setEditForm({ 
                          ...editForm, 
                          features: { ...editForm.features, apiAccess: checked }
                        })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePlan} className="bg-[#0052CC] hover:bg-[#0041A3]">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
